<template>
  <div class="fast backdrop-blur-sm">
    <div class="fast-container">
      <div class="fast-modal fast-fade overflow-hidden md:mt-16">
        <Loading v-show="loading" />

        <template v-if="!loading">
          <FastHeader @close="handleClose">
            <template #title>
              <h2 id="fast-title" class="m-0 text-center text-2xl font-medium leading-none text-neutral-700"></h2>
            </template>
            <template #wizard>
              <StepList v-if="!loading && !error" :current-step="currentStep" :steps="steps" :max-step="maxStep" />
            </template>
          </FastHeader>

          <div v-if="error && !loading" class="px-8 pb-16 text-center text-xl text-danger">
            <p>{{ error }}</p>
          </div>

          <form id="fast-form" ref="fastForm" method="post" action="/api/projects" @submit.prevent>
            <StepTitle
              v-if="currentStepIs('title')"
              :title="project.title"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepDescription
              v-if="currentStepIs('description')"
              :description="project.description"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepCategory
              v-if="currentStepIs('category')"
              :project="project"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepSelect
              v-if="currentStepIs('budget')"
              :step-title="`Quel est votre budget approximatif\u00A0?`"
              :list="budgets"
              :item-type="'budget'"
              :selected-item="project.budget"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepSelect
              v-if="currentStepIs('need')"
              :step-title="`Quelle est l'urgence de votre projet\u00A0?`"
              :list="needs"
              :item-type="'need'"
              :selected-item="project.need"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepSelect
              v-if="currentStepIs('profile')"
              :step-title="`Quel est le profil de votre entreprise\u00A0?`"
              :list="profiles"
              :item-type="'profile'"
              :selected-item="project.profile"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepRegistration
              v-if="currentStepIs('registration')"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepContactUpdate
              v-if="currentStepIs('contactUpdate')"
              @attributeChange="handleAttributeChange"
              @contactValidityChange="contactValidity = $event"
              @prevStep="prevStep"
              @nextStep="nextStep"
            />
            <StepProjectOption
              v-if="currentStepIs('projectOption')"
              @attributeChange="handleAttributeChange"
              @prevStep="prevStep"
              @done="handleDone"
            />
          </form>
        </template>
      </div>
      <MarketingMessage
        v-if="!loading && !error"
        :current-step="currentStep"
        :steps="steps"
        :max-step="maxStep"
        class="mt-6"
      />
    </div>
  </div>
</template>

<script>
/* global dataLayer */
import axios from 'axios'
import remove from 'lodash/remove'
import mapKeys from 'lodash/mapKeys'
import omit from 'lodash/omit'
import snakeCase from 'lodash/snakeCase'

import Loading from './components/Loading.vue'
import StepList from './components/StepList.vue'

import StepTitle from './components/StepTitle.vue'
import StepDescription from './components/StepDescription.vue'
import StepCategory from './components/StepCategory.vue'
import StepSelect from './components/StepSelect.vue'
import StepRegistration from './components/StepRegistration.vue'
import StepContactUpdate from './components/StepContactUpdate.vue'
import StepProjectOption from './components/StepProjectOption.vue'

import MarketingMessage from './components/MarketingMessage.vue'

import budgetsJSON from './database/budgets.json'
import needsJSON from './database/needs.json'
import profilesJSON from './database/profiles.json'
import FastHeader from '../shared/components/FastHeader.vue'

export default {
  name: 'TheFast',
  components: {
    Loading,
    StepList,
    StepTitle,
    StepDescription,
    StepCategory,
    StepSelect,
    StepRegistration,
    StepContactUpdate,
    StepProjectOption,
    MarketingMessage,
    FastHeader
  },
  props: {
    source: {
      type: String,
      default: null,
      required: false
    },
    userSignedIn: {
      type: Boolean,
      required: true
    }
  },
  data() {
    const steps = [
      'title',
      'description',
      'category',
      'budget',
      'need',
      'profile',
      'registration',
      'contactUpdate',
      'projectOption'
    ]

    if (this.userSignedIn) remove(steps, (v, i, a) => v === 'registration')

    return {
      mounted: false,
      project: {
        title: '',
        description: '',
        categoryId: null,
        budget: '',
        profile: '',
        need: '',
        projectOptionId: null,
        userId: null,
        startedAsGuest: true
      },
      budgets: budgetsJSON,
      needs: needsJSON,
      profiles: profilesJSON,
      currentStep: '',
      steps,
      maxStep: steps[0],
      loading: false,
      error: null,
      contactValidity: null
    }
  },
  computed: {
    nextStepIndex() {
      return this.steps.indexOf(this.currentStep) + 1
    },
    prevStepIndex() {
      return this.steps.indexOf(this.currentStep) - 1
    },
    maxStepIndex() {
      return this.steps.indexOf(this.maxStep)
    },
    projectParams() {
      return mapKeys(omit(this.project, ['userId']), (v, k) => snakeCase(k))
    },
    registered() {
      return this.userSignedIn || this.project.userId !== null
    },
    stepIsSkippable() {
      return (
        (this.currentStepIs('registration') && this.registered) ||
        (this.currentStepIs('contactUpdate') && this.contactValidity)
      )
    },
    analyticsUrl() {
      return `/fast/${this.currentStep}${this.source ? `?source=${this.source}` : ''}`
    }
  },
  watch: {
    currentStep() {
      this.matomoScanForForms()
    }
  },
  mounted() {
    this.currentStep = this.steps[0]
    this.mounted = true
    this.authorize()
    this.checkContactValidity()
    this.analyticsSendEvent()
    this.matomoScanForForms()
  },
  methods: {
    currentStepIs(step) {
      return this.mounted && this.currentStep === step && !this.loading && !this.error
    },
    nextStep() {
      if (this.nextStepIndex >= this.steps.length) {
        return
      }
      const next = this.steps[this.nextStepIndex]
      if (this.nextStepIndex > this.maxStepIndex) this.maxStep = next
      this.currentStep = next

      if (this.stepIsSkippable) this.nextStep()

      this.analyticsSendEvent()
      this.$nextTick(() => document.querySelector('.fast *[autofocus]')?.focus())
    },
    prevStep() {
      if (this.prevStepIndex < 0) {
        return
      }
      this.currentStep = this.steps[this.prevStepIndex]

      if (this.stepIsSkippable) this.prevStep()

      this.analyticsSendEvent()
      this.$nextTick(() => document.querySelector('.fast *[autofocus]')?.focus())
    },
    authorize() {
      axios.get('/api/projects/new').catch(error => {
        const data = error.response.data
        if (data.error) {
          this.error = data.error
        } else {
          this.error = 'Une erreur inattendue est survenue. Si le problème persiste veuillez nous contacter.'
        }
      })
    },
    checkContactValidity() {
      // Will be handled by the registration step
      if (!this.userSignedIn) this.contactValidity = true
      else {
        axios.get('/api/current/user_contacts').then(response => {
          this.contactValidity = response.data.zip_code && response.data.country_code
        })
      }
    },
    analyticsSendEvent() {
      this.matomoSendEvent()
      this.gtmSendEvent()
    },
    matomoSendEvent() {
      window._paq?.push(['setCustomUrl', this.analyticsUrl])
      window._paq?.push(['trackPageView'])
    },
    gtmSendEvent() {
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({ event: 'happen', step: this.currentStep, source: this.source, pageview: this.analyticsUrl })
      }
    },
    matomoScanForForms() {
      if (this.$refs.fastForm) window._paq?.push(['FormAnalytics::scanForForms', this.$refs.fastForm])
    },
    handleClose() {
      this.$emit('close')
    },
    handleAttributeChange(attribute, value) {
      this.project[attribute] = value
    },
    handleDone() {
      this.loading = true

      axios
        .post('/api/projects', {
          project: this.projectParams
        })
        .then(response => {
          window.location.replace(response.data.redirect_to)
        })
        .catch(error => {
          this.loading = false
          const data = error.response.data
          if (data.error) {
            this.error = data.error
          } else {
            this.error = 'Une erreur inattendue est survenue. Si le problème persiste veuillez nous contacter.'
          }
        })
    }
  }
}
</script>

<style lang="postcss">
.fast-modal,
.fast-help-modal {
  @apply rounded-xl shadow;
}
</style>

<style lang="scss">
.fast-container {
  width: 100%;
  padding: 5px;
}

@media (min-width: 768px) {
  .fast-container {
    width: 730px;
    padding: 0;
    margin: 8px auto;
  }
}

.fast {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  // Due to cssnano z-index is reduced to 2 after precompilation.
  // See (quick) fix in app/assets/stylesheets/application.scss
  z-index: 9999;
  background-color: rgba(#111, 0);
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  overflow-y: auto;
  animation-duration: 320ms;
  animation-fill-mode: both;
  animation-name: fastFadeInBackground;
}

@keyframes fastFadeInBackground {
  from {
    background-color: rgba(#111, 0);
  }

  to {
    background-color: rgba(#111, 0.82);
  }
}

.fast-fade {
  animation-duration: 320ms;
  animation-fill-mode: both;
  animation-name: fadeIn;
}

.fast-modal {
  position: relative;
  z-index: 10;
}

.fast-modal,
.fast-help-modal {
  width: 100%;
  background-color: #fff;
  margin: 0 auto;
}

.fast-modal-content {
  margin: 2rem;

  .btn-link {
    display: block;
    font-weight: 500;
    margin: 0 auto 16px;
  }
}

.fast-modal-content .btn-toolbar {
  margin-bottom: 8px;

  .btn {
    font-size: 16px;
    margin-right: 8px;
    margin-bottom: 8px;
  }
}

.fast-modal-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  margin-bottom: 16px;

  .btn {
    font-size: 16px;
    margin: 0 8px 8px 0;
  }
}

@media (min-width: 680px) {
  .fast-modal-btns {
    flex-direction: row;
  }
}
</style>
